import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import NotificationBell from '../components/NotificationBell';
import { 
  User, 
  Building2, 
  Briefcase, 
  MapPin, 
  DollarSign, 
  Target,
  Activity,
  FileText,
  Pencil,
  Check,
  X,
  Sparkles,
  Loader2,
  ChevronRight,
  RefreshCw,
  ArrowLeft,
  HelpCircle,
  BookOpen,
  Award,
  Beaker,
  Clock,
  BadgeCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Profile Help Modal Component
const ProfileHelpModal = ({ isOpen, onClose, onNavigateToHelp }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-lime-600" />
            My Profile
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">What is this page?</h4>
            <p className="text-sm text-slate-600">
              This page shows your complete career identity — your preferences, experience, and what you're looking for. It's the foundation of how our AI matches you with opportunities.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">Why does it matter?</h4>
            <p className="text-sm text-slate-600">
              Every detail contributes to your V-FIT score. Industries, functions, company preferences, and dealbreakers all help filter out unsuitable roles and surface the best matches.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Tips</h4>
            <ul className="space-y-2">
              {[
                'Hover over any card to quickly edit that section',
                'Generate an AI summary to see how we understand your profile',
                'Your dealbreakers are hard filters — be selective',
                'Review your profile periodically as your goals evolve'
              ].map((tip, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-4 w-4 text-lime-500 mt-0.5 flex-shrink-0" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>
          <div className="pt-3 border-t border-slate-100">
            <button
              onClick={onNavigateToHelp}
              className="text-sm text-lime-600 hover:text-lime-700 font-medium flex items-center gap-1"
            >
              <BookOpen className="h-4 w-4" />
              View full Help Center
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Animation variants
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }
  })
};

// Preference Card Component
const PreferenceCard = ({ 
  title, 
  icon: Icon, 
  items, 
  emptyText = "Not set",
  onEdit,
  sectionIndex,
  delay = 0
}) => {
  const hasItems = items && items.length > 0;
  const displayItems = Array.isArray(items) ? items : (items ? [items] : []);
  
  return (
    <motion.div
      custom={delay}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg hover:border-slate-300 transition-all duration-300 group"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center group-hover:bg-lime-100 transition-colors">
            <Icon className="h-5 w-5 text-slate-600 group-hover:text-lime-700 transition-colors" strokeWidth={1.5} />
          </div>
          <h3 className="font-medium text-slate-900">{title}</h3>
        </div>
        <button
          onClick={() => onEdit(sectionIndex)}
          className="opacity-0 group-hover:opacity-100 p-2 rounded-lg hover:bg-slate-100 transition-all"
          data-testid={`edit-${title.toLowerCase().replace(/\s+/g, '-')}-btn`}
        >
          <Pencil className="h-4 w-4 text-slate-400" />
        </button>
      </div>
      
      {hasItems ? (
        <div className="flex flex-wrap gap-2">
          {displayItems.slice(0, 5).map((item, idx) => (
            <span 
              key={idx}
              className="px-3 py-1.5 bg-slate-50 text-slate-700 text-sm rounded-full border border-slate-100"
            >
              {item}
            </span>
          ))}
          {displayItems.length > 5 && (
            <span className="px-3 py-1.5 text-slate-500 text-sm">
              +{displayItems.length - 5} more
            </span>
          )}
        </div>
      ) : (
        <p className="text-slate-400 text-sm italic">{emptyText}</p>
      )}
    </motion.div>
  );
};

// AI Summary Card Component
const AISummaryCard = ({ 
  summary, 
  isLoading, 
  isEditing, 
  editedSummary,
  onEdit,
  onSave,
  onCancel,
  onChange,
  onRegenerate,
  isGenerating
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="bg-gradient-to-br from-evergreen-950 to-evergreen-900 rounded-2xl p-8 text-white relative overflow-hidden"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-lime-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-lime-500/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2" />
      
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-lime-400" />
            <span className="text-sm font-medium text-lime-300">AI-Generated Summary</span>
          </div>
          {summary && !isEditing && (
            <div className="flex items-center gap-2">
              <button
                onClick={onRegenerate}
                disabled={isGenerating}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                title="Regenerate summary"
              >
                <RefreshCw className={`h-4 w-4 text-white/70 hover:text-white ${isGenerating ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onEdit}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                data-testid="edit-summary-btn"
              >
                <Pencil className="h-4 w-4 text-white/70 hover:text-white" />
              </button>
            </div>
          )}
        </div>
        
        {isLoading ? (
          <div className="flex items-center gap-3 py-4">
            <Loader2 className="h-5 w-5 animate-spin text-lime-400" />
            <span className="text-white/80">Analyzing your profile...</span>
          </div>
        ) : isEditing ? (
          <div className="space-y-4">
            <Textarea
              value={editedSummary}
              onChange={(e) => onChange(e.target.value)}
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40 min-h-[120px] resize-none focus:border-lime-400 focus:ring-lime-400/20"
              placeholder="Write your career summary in first person..."
              data-testid="summary-textarea"
            />
            <div className="flex items-center gap-2 justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={onCancel}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={onSave}
                className="bg-lime-500 hover:bg-lime-400 text-evergreen-950"
                data-testid="save-summary-btn"
              >
                <Check className="h-4 w-4 mr-1" />
                Save
              </Button>
            </div>
          </div>
        ) : summary ? (
          <blockquote className="text-lg md:text-xl font-light leading-relaxed text-white/90 italic">
            "{summary}"
          </blockquote>
        ) : (
          <div className="text-center py-6">
            <p className="text-white/60 mb-4">Let AI summarize your career preferences</p>
            <Button
              onClick={onRegenerate}
              disabled={isGenerating}
              className="bg-lime-500 hover:bg-lime-400 text-evergreen-950"
              data-testid="generate-summary-btn"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Summary
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Identity Card Component
const IdentityCard = ({ profile, candidate }) => {
  const initials = candidate?.email?.substring(0, 2).toUpperCase() || 'BC';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white border border-slate-200 rounded-2xl p-8"
    >
      <div className="flex items-start gap-6">
        {/* Avatar */}
        <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-evergreen-950 to-evergreen-800 flex items-center justify-center text-white text-2xl font-heading flex-shrink-0">
          {initials}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-heading text-slate-900 mb-1 truncate">
            {profile?.current_title || 'Life Sciences Professional'}
          </h1>
          
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-slate-500">
            {(profile?.location_city || profile?.location_state) && (
              <span className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {[profile.location_city, profile.location_state].filter(Boolean).join(', ')}
              </span>
            )}
            {profile?.years_experience && (
              <span>{profile.years_experience} experience</span>
            )}
          </div>
          
          {profile?.summary && (
            <p className="mt-3 text-slate-600 text-sm line-clamp-2">
              {profile.summary}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Expertise Tags Card Component - Shows parsed resume intelligence
const ExpertiseTagsCard = ({ insights, isLoading, onReanalyze, isReanalyzing }) => {
  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="bg-white border border-slate-200 rounded-2xl p-6"
      >
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          <span className="text-slate-500">Analyzing your resume...</span>
        </div>
      </motion.div>
    );
  }

  if (!insights || !insights.has_insights) {
    return null; // Don't show if no insights
  }

  const { top_skills, therapeutic_areas, industry_types, company_history, career_span_years, last_title } = insights;

  // Check if we have any meaningful data to display
  const hasData = (top_skills?.length > 0) || (therapeutic_areas?.length > 0) || 
                  (industry_types?.length > 0) || (company_history?.length > 0);
  
  if (!hasData) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
      className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-2xl p-6 relative overflow-hidden"
    >
      {/* Decorative background */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-lime-100/50 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      
      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-lime-100 flex items-center justify-center">
              <BadgeCheck className="h-4 w-4 text-lime-700" />
            </div>
            <div>
              <h3 className="font-medium text-slate-900">Career Snapshot</h3>
              <p className="text-xs text-slate-500">Extracted from your resume</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Re-analyze button */}
            <button
              onClick={onReanalyze}
              disabled={isReanalyzing}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50"
              title="Re-analyze resume"
              data-testid="reanalyze-resume-btn"
            >
              <RefreshCw className={`h-4 w-4 text-slate-500 hover:text-slate-700 ${isReanalyzing ? 'animate-spin' : ''}`} />
            </button>
            {career_span_years && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-evergreen-950 text-white rounded-full text-sm">
                <Clock className="h-3.5 w-3.5" />
                <span>{career_span_years} years</span>
              </div>
            )}
          </div>
        </div>

        {/* Last Title */}
        {last_title && (
          <div className="mb-5 pb-5 border-b border-slate-100">
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Current Role</p>
            <p className="text-lg font-medium text-slate-900">{last_title}</p>
          </div>
        )}

        {/* Tags Grid */}
        <div className="space-y-4">
          {/* Top Skills */}
          {top_skills?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Award className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium text-slate-700">Skills</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {top_skills.slice(0, 6).map((skill, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1.5 bg-amber-50 text-amber-800 text-sm rounded-full border border-amber-100 font-medium"
                  >
                    {skill}
                  </span>
                ))}
                {top_skills.length > 6 && (
                  <span className="px-3 py-1.5 text-slate-500 text-sm">
                    +{top_skills.length - 6} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Therapeutic Areas */}
          {therapeutic_areas?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Beaker className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-medium text-slate-700">Therapeutic Areas</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {therapeutic_areas.map((area, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1.5 bg-purple-50 text-purple-800 text-sm rounded-full border border-purple-100 font-medium capitalize"
                  >
                    {area}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Industry Types */}
          {industry_types?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-slate-700">Industry Background</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {industry_types.map((industry, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1.5 bg-blue-50 text-blue-800 text-sm rounded-full border border-blue-100 font-medium capitalize"
                  >
                    {industry}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Company History */}
          {company_history?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Briefcase className="h-4 w-4 text-slate-600" />
                <span className="text-sm font-medium text-slate-700">Companies</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {company_history.slice(0, 5).map((company, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1.5 bg-slate-100 text-slate-700 text-sm rounded-full border border-slate-200"
                  >
                    {company}
                  </span>
                ))}
                {company_history.length > 5 && (
                  <span className="px-3 py-1.5 text-slate-500 text-sm">
                    +{company_history.length - 5} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer note */}
        <div className="mt-5 pt-4 border-t border-slate-100">
          <p className="text-xs text-slate-500 flex items-center gap-1">
            <Sparkles className="h-3 w-3" />
            This data enhances your V-FIT matching score
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default function ProfilePage() {
  const { authAxios, profile, preferences, user } = useAuth();
  const navigate = useNavigate();
  
  const [summary, setSummary] = useState(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSummary, setEditedSummary] = useState('');
  const [diseaseAreas, setDiseaseAreas] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [showHelp, setShowHelp] = useState(false);
  const [resumeInsights, setResumeInsights] = useState(null);
  const [isLoadingInsights, setIsLoadingInsights] = useState(true);
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  
  // Load profile summary
  useEffect(() => {
    const loadSummary = async () => {
      try {
        const response = await authAxios.get('/candidates/profile-summary');
        if (response.data.summary) {
          setSummary(response.data.summary);
        }
      } catch (error) {
        console.error('Failed to load summary:', error);
      } finally {
        setIsLoadingSummary(false);
      }
    };
    loadSummary();
  }, [authAxios]);
  
  // Load disease areas
  useEffect(() => {
    const loadDiseaseAreas = async () => {
      try {
        const response = await authAxios.get('/candidates/disease-areas');
        setDiseaseAreas(response.data.disease_areas || []);
      } catch (error) {
        console.error('Failed to load disease areas:', error);
      }
    };
    loadDiseaseAreas();
  }, [authAxios]);
  
  // Load resumes
  useEffect(() => {
    const loadResumes = async () => {
      try {
        const response = await authAxios.get('/candidates/resumes');
        setResumes(response.data.resumes || []);
      } catch (error) {
        console.error('Failed to load resumes:', error);
      }
    };
    loadResumes();
  }, [authAxios]);

  // Load resume insights (parsed data)
  useEffect(() => {
    const loadInsights = async () => {
      try {
        const response = await authAxios.get('/candidates/resumes/insights');
        setResumeInsights(response.data);
      } catch (error) {
        console.error('Failed to load resume insights:', error);
      } finally {
        setIsLoadingInsights(false);
      }
    };
    loadInsights();
  }, [authAxios]);

  // Re-analyze resume handler
  const handleReanalyzeResume = async () => {
    if (!resumeInsights?.resume_id) {
      toast.error('No resume found to analyze');
      return;
    }
    
    setIsReanalyzing(true);
    try {
      const response = await authAxios.post(`/candidates/resumes/${resumeInsights.resume_id}/parse`);
      
      if (response.data.parsed) {
        // Update insights with new data
        setResumeInsights({
          has_insights: true,
          resume_id: resumeInsights.resume_id,
          ...response.data.parsed_data
        });
        toast.success('Resume re-analyzed successfully!');
      } else {
        toast.error('Could not extract data from resume');
      }
    } catch (error) {
      console.error('Failed to re-analyze resume:', error);
      toast.error('Failed to re-analyze resume');
    } finally {
      setIsReanalyzing(false);
    }
  };
  
  const handleGenerateSummary = async () => {
    setIsGenerating(true);
    try {
      const response = await authAxios.post('/candidates/profile-summary/generate');
      setSummary(response.data.summary);
      toast.success('Summary generated!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate summary';
      toast.error(message);
    } finally {
      setIsGenerating(false);
    }
  };
  
  const handleEditSummary = () => {
    setEditedSummary(summary || '');
    setIsEditing(true);
  };
  
  const handleSaveSummary = async () => {
    try {
      const formData = new FormData();
      formData.append('summary', editedSummary);
      
      await authAxios.put('/candidates/profile-summary', formData);
      setSummary(editedSummary);
      setIsEditing(false);
      toast.success('Summary updated!');
    } catch (error) {
      toast.error('Failed to save summary');
    }
  };
  
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedSummary('');
  };
  
  const handleEditSection = (sectionIndex) => {
    // Navigate to onboarding at the specific section
    navigate('/onboarding', { state: { startSection: sectionIndex } });
  };
  
  // Prepare preference data
  const preferenceCards = [
    {
      title: 'Industries',
      icon: Building2,
      items: preferences?.desired_industries || [],
      sectionIndex: 1,
      emptyText: 'No industries selected'
    },
    {
      title: 'Functions',
      icon: Briefcase,
      items: preferences?.desired_functions || [],
      sectionIndex: 1,
      emptyText: 'No functions selected'
    },
    {
      title: 'Company Type',
      icon: Building2,
      items: [
        ...(preferences?.company_size_preferences || []).map(s => `${s} employees`),
        ...(preferences?.company_stage_preferences || []),
        ...(preferences?.ownership_preferences || [])
      ].filter(Boolean),
      sectionIndex: 2,
      emptyText: 'No preferences set'
    },
    {
      title: 'Work Style',
      icon: MapPin,
      items: [
        ...(preferences?.work_model_preferences || []),
        ...(preferences?.geography_preferences || [])
      ].filter(Boolean),
      sectionIndex: 3,
      emptyText: 'No preferences set'
    },
    {
      title: 'Compensation',
      icon: DollarSign,
      items: [
        preferences?.base_salary_band,
        preferences?.equity_preference
      ].filter(Boolean),
      sectionIndex: 4,
      emptyText: 'Not specified'
    },
    {
      title: 'Disease Areas',
      icon: Activity,
      items: diseaseAreas.map(d => d.disease_area),
      sectionIndex: 6,
      emptyText: 'No areas selected'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm font-medium">Back to Dashboard</span>
            </Link>
            <Link to="/" className="text-xl font-heading text-evergreen-950">
              <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
            </Link>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-slate-500 hover:text-lime-600"
                onClick={() => setShowHelp(true)}
                data-testid="profile-help-btn"
              >
                <HelpCircle className="h-4 w-4" />
              </Button>
              <NotificationBell />
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading text-slate-900 mb-2">My Profile</h1>
          <p className="text-slate-500">Here's what we know about your career preferences</p>
        </div>
        
        {/* Identity Card */}
        <div className="mb-8">
          <IdentityCard profile={profile} candidate={user} />
        </div>
        
        {/* AI Summary Card */}
        <div className="mb-8">
          <AISummaryCard
            summary={summary}
            isLoading={isLoadingSummary}
            isEditing={isEditing}
            editedSummary={editedSummary}
            onEdit={handleEditSummary}
            onSave={handleSaveSummary}
            onCancel={handleCancelEdit}
            onChange={setEditedSummary}
            onRegenerate={handleGenerateSummary}
            isGenerating={isGenerating}
          />
        </div>

        {/* Career Snapshot / Expertise Tags - from parsed resume */}
        <div className="mb-8">
          <ExpertiseTagsCard 
            insights={resumeInsights} 
            isLoading={isLoadingInsights}
            onReanalyze={handleReanalyzeResume}
            isReanalyzing={isReanalyzing}
          />
        </div>
        
        {/* Section: What We're Looking For */}
        <div className="mb-6">
          <h2 className="text-lg font-medium text-slate-900 mb-1">Your Preferences</h2>
          <p className="text-sm text-slate-500">Click any card to update your preferences</p>
        </div>
        
        {/* Preference Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {preferenceCards.map((card, index) => (
            <PreferenceCard
              key={card.title}
              {...card}
              onEdit={handleEditSection}
              delay={index}
            />
          ))}
        </div>
        
        {/* Dealbreakers Section */}
        {(preferences?.raw_ideal_role || preferences?.raw_dealbreakers) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8"
          >
            {preferences?.raw_ideal_role && (
              <div className="bg-white border border-slate-200 rounded-2xl p-6 group hover:shadow-lg hover:border-slate-300 transition-all">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-lime-600" />
                    <h3 className="font-medium text-slate-900">What I'm Looking For</h3>
                  </div>
                  <button
                    onClick={() => handleEditSection(5)}
                    className="opacity-0 group-hover:opacity-100 p-2 rounded-lg hover:bg-slate-100 transition-all"
                  >
                    <Pencil className="h-4 w-4 text-slate-400" />
                  </button>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">{preferences.raw_ideal_role}</p>
              </div>
            )}
            
            {preferences?.raw_dealbreakers && (
              <div className="bg-white border border-slate-200 rounded-2xl p-6 group hover:shadow-lg hover:border-slate-300 transition-all">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <X className="h-5 w-5 text-red-500" />
                    <h3 className="font-medium text-slate-900">Dealbreakers</h3>
                  </div>
                  <button
                    onClick={() => handleEditSection(5)}
                    className="opacity-0 group-hover:opacity-100 p-2 rounded-lg hover:bg-slate-100 transition-all"
                  >
                    <Pencil className="h-4 w-4 text-slate-400" />
                  </button>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">{preferences.raw_dealbreakers}</p>
              </div>
            )}
          </motion.div>
        )}
        
        {/* Resume Section */}
        {resumes.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white border border-slate-200 rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-slate-600" />
                <h3 className="font-medium text-slate-900">Resume</h3>
              </div>
              <Link
                to="/resumes"
                className="text-sm text-lime-600 hover:text-lime-700 font-medium flex items-center gap-1"
              >
                Manage
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-slate-400" />
              </div>
              <div>
                <p className="font-medium text-slate-900">{resumes[0]?.label || 'Resume'}</p>
                <p className="text-sm text-slate-500">
                  {resumes[0]?.is_primary && 'Primary • '}
                  Uploaded {new Date(resumes[0]?.uploaded_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </motion.div>
        )}
        
        {/* Bottom Actions */}
        <div className="mt-8 pt-8 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">
            We'll only reach out when we find opportunities that match your preferences.
          </p>
          <Button
            onClick={() => navigate('/onboarding')}
            variant="outline"
            className="border-slate-200"
          >
            Edit Full Profile
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </main>

      {/* Profile Help Modal */}
      <ProfileHelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        onNavigateToHelp={() => {
          setShowHelp(false);
          navigate('/help');
        }}
      />
    </div>
  );
}
